import { Tirage } from "@/api/tirages/tirage.api";
import {
  AdvancedSearchConfig,
  flattenAdvancedSearchConfig,
  isFromThisTable,
  isStringFieldConfig,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import {
  getHiddenSearchFieldKeys,
  resourceToTable,
} from "@/config/field-visibility/search-field-visibility";
import {
  Artwork,
  Auction,
  Audiovisual,
  Author,
  Bibliography,
  Collector,
  Document,
  Exhibition,
  Multipleprint,
  Multiplesample,
  Photography,
  SculptureEdition,
  Writing,
} from "@/lib/types";
import { XIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { HtmlCell } from "./CustomCell";

export type GlobalSearchProps = {
  config: AdvancedSearchConfig;
  data: Array<
    | Artwork
    | Bibliography
    | Exhibition
    | Photography
    | Document
    | Writing
    | Multipleprint
    | Multiplesample
    | Auction
    | Author
    | Audiovisual
    | Collector
    | Tirage
    | SculptureEdition
  >;
  /** Advanced-search resource key; masked fiche fields are excluded from search. */
  resource?: string;
};

export const GlobalSearch = ({ config, data, resource }: GlobalSearchProps) => {
  const { t } = useTranslation();
  const { setGlobalSearch, globalSearch } = useAdvancedSearch();

  const [searchContent, setSearchContent] = useState(globalSearch);
  const [isOpen, setIsOpen] = useState(false);
  const [resultMatching, setResultMatching] = useState<
    { key: string; value: string }[]
  >([]);

  const flatAndFilteredConfig = useMemo(() => {
    const hidden = getHiddenSearchFieldKeys(config, resourceToTable(resource));
    return flattenAdvancedSearchConfig(config)
      .filter(isStringFieldConfig)
      .filter(isFromThisTable)
      .filter((field) => !hidden.has(field.key));
  }, [config, resource]);

  const getResultMatching = useCallback(
    (newSearchContent: string) => {
      if (newSearchContent === "") {
        setIsOpen(false);
        return [];
      }

      const result = new Map<string, { key: string; value: string }>();
      const searchLower = newSearchContent.toLowerCase();

      data.forEach((item) => {
        flatAndFilteredConfig.forEach((config) => {
          const key = config.key;
          const value = item[key as keyof typeof item] as string;
          if (
            typeof value === "string" &&
            value.toLowerCase().includes(searchLower)
          ) {
            const mapKey = `${key}-${value}`;
            if (!result.has(mapKey)) {
              result.set(mapKey, {
                key,
                value,
              });
            }
          }
        });
      });

      const results = Array.from(result.values());
      setIsOpen(results.length > 0);
      return results;
    },
    [data, flatAndFilteredConfig],
  );

  const onChange = useCallback(
    (newSearchContent: string) => {
      setSearchContent(newSearchContent);
      const results = getResultMatching(newSearchContent);
      setResultMatching(results);
      setGlobalSearch({
        searchContent: newSearchContent,
        stringConfig: flatAndFilteredConfig,
      });
    },
    [setGlobalSearch, flatAndFilteredConfig, getResultMatching],
  );

  const handleSelect = (item: { key: string; value: string }) => {
    setGlobalSearch({
      searchContent: item.value,
      stringConfig: [{ type: "string", key: item.key }],
    });
    setSearchContent(item.value);
    setIsOpen(false);
  };

  return (
    <div className="relative w-[500px] flex justify-end">
      <Command
        shouldFilter={false}
        className="border rounded-md h-[40px] w-[400px]"
      >
        <CommandInput
          placeholder={t("search")}
          value={searchContent}
          onValueChange={onChange}
        />
        {searchContent && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
            onClick={() => onChange("")}
          >
            <XIcon className="w-4 h-4 text-muted-foreground" />
          </Button>
        )}

        {isOpen && (
          <CommandList className="absolute w-full top-10 z-10 left-0 bg-white border rounded-md mt-1 shadow-lg max-h-[300px] overflow-y-auto">
            <CommandEmpty>{t("no_results")}</CommandEmpty>
            <CommandGroup>
              {resultMatching.map((item) => (
                <CommandItem
                  key={`${item.key}-${item.value}`}
                  onSelect={() => handleSelect(item)}
                  value={`${item.key}-${item.value}`}
                  className="cursor-pointer flex flex-row items-start"
                >
                  <span className="font-medium text-sm">{t(item.key)}:</span>
                  <span className="text-sm text-muted-foreground">
                    <HtmlCell value={item.value} />
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        )}
      </Command>
    </div>
  );
};
